import React, { useState, useEffect, useMemo } from 'react';
// @ts-ignore
import { Award, ShieldCheck, Zap, Clock } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from 'recharts';
import { operationalDashboardService } from '../../services/operationalDashboard';

interface Props {
  filters: { periodo: string; dataInicio?: string; dataFim?: string };
}

function formatSeconds(s: number): string {
  if (!s || s <= 0) return '-';
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  if (m > 0) return `${m}m ${sec.toString().padStart(2, '0')}s`;
  return `${sec}s`;
}

const MEDAL_COLORS = ['#facc15', '#cbd5e1', '#f97316'];

const OperationalDashboardConferentes: React.FC<Props> = ({ filters }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableFilters = useMemo(() => filters, [filters.periodo, filters.dataInicio, filters.dataFim]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await operationalDashboardService.getConferentes(stableFilters);
        setData(res.data);
      } catch (err: unknown) {
        setError('Erro ao carregar dados dos conferentes. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [stableFilters]);

  if (loading) {
    return <div className="op-dashboard-loading"><div className="op-dashboard-spinner" />Carregando conferentes...</div>;
  }

  if (error) {
    return <div className="op-dashboard-error">{error}</div>;
  }

  if (!data) return null;

  const ranking = data.ranking_produtividade || [];
  const qualidade = data.ranking_qualidade || [];
  const produtividade = data.produtividade || [];
  const tempoMedio = data.tempo_medio || [];
  const maxEtiquetas = Math.max(...ranking.map((r: any) => r.total_etiquetas || 0), 1);

  return (
    <>
      {/* Ranking de Produtividade */}
      <h2 className="op-dashboard-section-title">
        <Award size={18} /> Ranking de Conferentes
      </h2>

      {ranking.length > 0 ? (
        <div className="op-dashboard-card-section">
          <table className="op-dashboard-table">
            <thead>
              <tr>
                <th style={{ width: 60 }}>Pos.</th>
                <th>Conferente</th>
                <th>Etiquetas</th>
                <th>Cargas</th>
                <th>Divergências</th>
                <th>Taxa Erro</th>
                <th>Volume</th>
              </tr>
            </thead>
            <tbody>
              {ranking.map((r: any, i: number) => (
                <tr key={r.conferente_id}>
                  <td>
                    <span className="op-dashboard-medal" style={{
                      background: i < 3 ? `rgba(${i === 0 ? '234,179,8' : i === 1 ? '148,163,184' : '249,115,22'},0.2)` : 'rgba(148,163,184,0.1)',
                      color: i < 3 ? MEDAL_COLORS[i] : '#94a3b8'
                    }}>
                      {i + 1}º
                    </span>
                  </td>
                  <td style={{ fontWeight: 600 }}>{r.conferente_nome}</td>
                  <td>{r.total_etiquetas.toLocaleString('pt-BR')}</td>
                  <td>{r.total_cargas}</td>
                  <td>
                    <span className={`op-dashboard-badge ${r.total_divergencias > 0 ? 'red' : 'green'}`}>
                      {r.total_divergencias}
                    </span>
                  </td>
                  <td>
                    <span style={{ color: r.taxa_erro > 5 ? '#f87171' : r.taxa_erro > 2 ? '#facc15' : '#4ade80' }}>
                      {r.taxa_erro}%
                    </span>
                  </td>
                  <td style={{ width: 200 }}>
                    <div className="op-dashboard-bar-track">
                      <div
                        className="op-dashboard-bar-fill"
                        style={{ width: `${(r.total_etiquetas / maxEtiquetas) * 100}%` }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="op-dashboard-empty">Sem dados de conferentes no período selecionado</div>
      )}

      {/* Ranking de Qualidade */}
      {qualidade.length > 0 && (
        <>
          <h2 className="op-dashboard-section-title">
            <ShieldCheck size={18} /> Ranking de Qualidade
          </h2>
          <div className="op-dashboard-card-section">
            <table className="op-dashboard-table">
              <thead>
                <tr>
                  <th>Conferente</th>
                  <th>Duplicidades</th>
                  <th>Divergências</th>
                  <th>Total Leituras</th>
                  <th>Taxa de Erro</th>
                </tr>
              </thead>
              <tbody>
                {qualidade.map((r: any) => (
                  <tr key={r.conferente_id}>
                    <td style={{ fontWeight: 600 }}>{r.conferente_nome}</td>
                    <td>
                      <span className={`op-dashboard-badge ${r.duplicidades > 0 ? 'yellow' : 'green'}`}>
                        {r.duplicidades}
                      </span>
                    </td>
                    <td>
                      <span className={`op-dashboard-badge ${r.divergencias > 0 ? 'red' : 'green'}`}>
                        {r.divergencias}
                      </span>
                    </td>
                    <td>{r.total_leituras.toLocaleString('pt-BR')}</td>
                    <td>
                      <span style={{
                        fontWeight: 700,
                        color: r.taxa_erro > 5 ? '#f87171' : r.taxa_erro > 2 ? '#facc15' : '#4ade80'
                      }}>
                        {r.taxa_erro}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Produtividade - Etiquetas/Hora */}
      {produtividade.length > 0 && (
        <>
          <h2 className="op-dashboard-section-title">
            <Zap size={18} /> Produtividade da Equipe (Etiquetas/Hora)
          </h2>
          <div className="op-dashboard-chart-container">
            <ResponsiveContainer width="100%" height={Math.min(700, Math.max(300, produtividade.length * 45))}>
              <BarChart data={produtividade.map((p: any) => ({
                nome: p.conferente_nome.length > 18 ? p.conferente_nome.substring(0, 18) + '...' : p.conferente_nome,
                etiquetas_hora: p.etiquetas_por_hora
              }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
                <XAxis type="number" stroke="#94a3b8" />
                <YAxis type="category" dataKey="nome" stroke="#94a3b8" width={140} tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 8, color: '#e2e8f0' }}
                  formatter={(value: any) => [`${value} etiq./h`, 'Produtividade']} />
                <Bar dataKey="etiquetas_hora" fill="#3b82f6" radius={[0, 4, 4, 0]} name="Etiquetas/Hora" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {/* Tempo Médio de Conferência */}
      {tempoMedio.length > 0 && (
        <>
          <h2 className="op-dashboard-section-title">
            <Clock size={18} /> Tempo Médio de Conferência por Conferente
          </h2>
          <div className="op-dashboard-card-section">
            <table className="op-dashboard-table">
              <thead>
                <tr>
                  <th>Conferente</th>
                  <th>Conferências</th>
                  <th>Tempo Médio</th>
                </tr>
              </thead>
              <tbody>
                {tempoMedio.map((r: any) => (
                  <tr key={r.conferente_id}>
                    <td style={{ fontWeight: 600 }}>{r.conferente_nome}</td>
                    <td>{r.total_conferencias}</td>
                    <td>
                      <span style={{
                        fontWeight: 600,
                        color: r.tempo_medio_segundos > 1800 ? '#f87171' : r.tempo_medio_segundos > 600 ? '#facc15' : '#4ade80'
                      }}>
                        {formatSeconds(r.tempo_medio_segundos)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
};

export default OperationalDashboardConferentes;
